/**
 * Página de Modelos de Contrato do CRM
 * 
 * CRUD completo de templates de contrato com editor TipTap
 * e sistema de variáveis para geração automática de contratos.
 * 
 * @release v1.0.0 - 2026-02-16
 */

import { useState, useMemo } from 'react';
import {
  FileText,
  Plus,
  Search,
  Edit,
  Trash2,
  MoreHorizontal,
  Copy,
  Loader2,
  FileSignature,
  History,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useContractTemplates } from '@/hooks/useContractTemplates';
import { ContractTemplateFormDialog } from '@/components/contracts/ContractTemplateFormDialog';
import { ContractTemplateVersionHistoryDialog } from '@/components/contracts/ContractTemplateVersionHistoryDialog';
import { CONTRACT_CATEGORIES, type ContractTemplate, type ContractTemplateInput } from '@/types/contracts';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { usePermissions } from '@/hooks/usePermissions';

export default function CrmContractTemplates() {
  // === Controle de permissões granular ===
  const { canCreate, canEdit, canDelete } = usePermissions();
  const MENU_KEY = 'crm_contract_templates';
  const hasCreatePermission = canCreate(MENU_KEY);
  const hasEditPermission = canEdit(MENU_KEY);
  const hasDeletePermission = canDelete(MENU_KEY);

  // Estado local
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ContractTemplate | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  // Estado para o modal de histórico de versões
  const [historyTemplate, setHistoryTemplate] = useState<ContractTemplate | null>(null);

  // Hook de dados
  const {
    templates,
    isLoading,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  } = useContractTemplates();

  // Filtragem
  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      const matchesSearch =
        !search ||
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.description?.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [templates, search, categoryFilter]);

  // Handlers
  const handleCreate = () => {
    setEditingTemplate(null);
    setFormOpen(true);
  };

  const handleEdit = (template: ContractTemplate) => {
    setEditingTemplate(template);
    setFormOpen(true);
  };

  const handleDuplicate = (template: ContractTemplate) => {
    createTemplate.mutate({
      name: `${template.name} (cópia)`,
      description: template.description || undefined,
      content_html: template.content_html,
      variables: template.variables,
      category: template.category,
    });
  };

  const handleSave = (input: ContractTemplateInput & { id?: string }) => {
    if (input.id) {
      updateTemplate.mutate(
        { ...input, id: input.id },
        { onSuccess: () => setFormOpen(false) }
      );
    } else {
      createTemplate.mutate(input, {
        onSuccess: () => setFormOpen(false),
      });
    }
  };

  const handleDelete = () => {
    if (deleteId) {
      deleteTemplate.mutate(deleteId, {
        onSuccess: () => setDeleteId(null),
      });
    }
  };

  // Label da categoria
  const getCategoryLabel = (value: string) =>
    CONTRACT_CATEGORIES.find((c) => c.value === value)?.label || value;

  return (
    <div className="flex flex-col h-full">
      {/* Header — padrão compacto */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Modelos de Contrato</h1>
        </div>
        {hasCreatePermission && (
          <Button size="xs" onClick={handleCreate} className="shrink-0">
            <Plus className="h-4 w-4 mr-1.5" />
            Novo Modelo
          </Button>
        )}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar modelos..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40 h-8 text-sm">
            <SelectValue placeholder="Categoria" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {CONTRACT_CATEGORIES.map((cat) => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredTemplates.length === 0 ? (
          /* Empty state */
          <Card className="flex flex-col items-center justify-center py-16 px-8 text-center">
            <div className="p-4 rounded-full bg-muted mb-4">
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-2">
              {search || categoryFilter !== 'all'
                ? 'Nenhum modelo encontrado'
                : 'Crie seu primeiro modelo'}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mb-6">
              {search || categoryFilter !== 'all'
                ? 'Tente ajustar os filtros de busca.'
                : 'Modelos de contrato permitem gerar PDFs automaticamente com dados do negócio. Use variáveis como {{nome_cliente}} para preenchimento automático.'}
            </p>
            {!search && categoryFilter === 'all' && hasCreatePermission && (
              <Button onClick={handleCreate} className="gap-2">
                <Plus className="h-4 w-4" />
                Criar Modelo
              </Button>
            )}
          </Card>
        ) : (
          /* Tabela de templates */
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Variáveis</TableHead>
                  <TableHead>Versão</TableHead>
                  <TableHead>Atualizado</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{template.name}</p>
                        {template.description && (
                          <p className="text-xs text-muted-foreground truncate max-w-xs">
                            {template.description}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{getCategoryLabel(template.category)}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {Array.isArray(template.variables) ? template.variables.length : 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      {/* Versão real baseada no máximo version_number do join com contract_template_versions */}
                      {(() => {
                        const versions = template.contract_template_versions;
                        const maxVer = versions && versions.length > 0
                          ? Math.max(...versions.map((v) => v.version_number))
                          : 1;
                        return (
                          <span className="text-sm font-mono text-muted-foreground">
                            v1.{maxVer}
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(template.updated_at), "dd/MM/yy HH:mm", { locale: ptBR })}
                      </span>
                    </TableCell>
                    {/* Status — Badge outline + StatusDot (padrão Departamentos/Perfis de Permissão) */}
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                      >
                        <StatusDot tone={template.is_active ? 'emerald' : 'muted'} />
                        {template.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {hasEditPermission && (
                            <DropdownMenuItem onClick={() => handleEdit(template)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                          )}
                          {hasCreatePermission && (
                            <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicar
                            </DropdownMenuItem>
                          )}
                          {/* Opção de histórico de versões - visualização */}
                          <DropdownMenuItem onClick={() => setHistoryTemplate(template)}>
                            <History className="h-4 w-4 mr-2" />
                            Histórico de Versões
                          </DropdownMenuItem>
                          {hasDeletePermission && (
                            <DropdownMenuItem
                              onClick={() => setDeleteId(template.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      {/* Dialog de formulário */}
      <ContractTemplateFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        template={editingTemplate}
        onSave={handleSave}
        isSaving={createTemplate.isPending || updateTemplate.isPending}
      />

      {/* Modal de Histórico de Versões */}
      {historyTemplate && (
        <ContractTemplateVersionHistoryDialog
          open={!!historyTemplate}
          onClose={() => setHistoryTemplate(null)}
          templateId={historyTemplate.id}
          templateName={historyTemplate.name}
        />
      )}

      {/* Dialog de confirmação de exclusão */}
      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir modelo de contrato?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O modelo será removido permanentemente.
              Contratos já gerados a partir deste modelo não serão afetados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
